import isEmpty from 'lodash/isEmpty';

import settings from '~/settings';
import { isClanPreloaded } from '~/helpers/clan';
import { isClanBattleType } from '~/helpers/ladder';
import { clanLadderSelector, clanSelector } from '~/store/selectors/clanSelectors';
import {
  clanMembersSelector,
  clanMembersStatisticsSelector,
  membersBattleTypesSelector,
  membersTeamNumberSelector,
} from '~/store/selectors/membersSelectors';
import { createAppSelector } from '~/store/utils/createSelector';

import type { ClanStatistics, WowsLadderType } from '~/Actions/ActionClanProfile';
import type { BATTLE_TYPES } from '~/constants';
import type { RootState } from '~/store';
import type { IClanMember, ITeamStatistics } from '~/types/declaration';

const getEffectiveClanId = (state: RootState, clanId: number) => {
  return clanId || (state.currentAccount.clanId as number);
};

const sumByNumberField = <T extends IClanMember>(items: T[], key: keyof T): number => {
  return items.reduce((sum, item) => {
    const value = item[key];
    if (typeof value !== 'number') {
      return sum;
    }
    return sum + value;
  }, 0);
};

const clanCurrentBattleTypeSelector = (state: RootState, clanId: number) => {
  const effectiveClanId = getEffectiveClanId(state, clanId);
  return state.members.battleTypes[effectiveClanId] || settings.defaultBattleType;
};

const isClanBattleStatistics = (
  currentBattleType: BATTLE_TYPES,
  clanStatistics: ClanStatistics | WowsLadderType | null,
): clanStatistics is WowsLadderType & ClanStatistics => {
  return isClanBattleType(currentBattleType) && !isEmpty(clanStatistics);
};

export const statisticsSelector = createAppSelector(
  clanSelector,
  clanMembersStatisticsSelector,
  clanLadderSelector,
  clanMembersSelector,
  clanCurrentBattleTypeSelector,
  (clan, currentClanStatistics, currentLadderStatistics, members = [], currentBattleType) => {
    const clanStatistics = currentClanStatistics ? currentClanStatistics.statistics : null;
    const isHiddenStatistics = currentClanStatistics ? currentClanStatistics.isHiddenStatistics : false;

    const hasBattleStatistics = isClanBattleStatistics(currentBattleType, clanStatistics);
    const ladderMaxPosition = hasBattleStatistics ? clanStatistics.max_position : null;

    const membersCount = !clan ? null : isClanPreloaded(clan) ? clan.membersCount : clan.members_count;

    const statistics: ITeamStatistics = {
      currentBattleType,
      totalMembers: membersCount,
      maxMembersCount: clan ? clan.maxMembersCount : null,

      averageBattlesCounts: null,
      averageExperiencePerBattle: null,
      averageDamagePerBattle: null,
      averageWinsPercentage: null,
      winsCount: null,

      leadingTeamNumber: hasBattleStatistics ? clanStatistics.leading_team_number : null,
      ratings: hasBattleStatistics ? clanStatistics.ratings : null,

      ladderBattlesCount: hasBattleStatistics ? clanStatistics.battles_count : null,
      ladderWinsPercent:
        hasBattleStatistics && currentLadderStatistics
          ? ((clanStatistics.wins_count || 0) / clanStatistics.battles_count) * 100
          : null,

      ladderMaxLeague: ladderMaxPosition ? ladderMaxPosition.league : null,
      ladderMaxDivision: ladderMaxPosition ? ladderMaxPosition.division : null,
      ladderMaxDivisionRating: ladderMaxPosition ? ladderMaxPosition.division_rating : null,

      ladderWinStreak: hasBattleStatistics ? clanStatistics.longest_winning_streak : null,
      ladderWinsCount: hasBattleStatistics ? clanStatistics.wins_count : null,
    };

    if (isHiddenStatistics) {
      return statistics;
    }

    if (clanStatistics) {
      statistics.averageBattlesCounts = clanStatistics.battles_count;
      statistics.averageExperiencePerBattle = clanStatistics.exp_per_battle;
      statistics.averageDamagePerBattle = clanStatistics.damage_per_battle;
      statistics.averageWinsPercentage = clanStatistics.wins_percentage;

      return statistics;
    }

    if (members.length === 0) {
      return statistics;
    }

    const battlesCount = sumByNumberField(members, 'battlesCount');
    if (battlesCount <= 0) {
      return statistics;
    }

    const wins = members.reduce((sum, item) => sum + (item.battlesCount * item.winsPercentage) / 100, 0);
    const totalDamage = members.reduce((sum, item) => sum + item.damagePerBattle * item.battlesCount, 0);
    const totalExperience = members.reduce((sum, item) => sum + item.expPerBattle * item.battlesCount, 0);

    statistics.averageBattlesCounts = battlesCount / members.length;
    statistics.averageWinsPercentage = (wins / battlesCount) * 100;
    statistics.averageExperiencePerBattle = totalExperience / battlesCount;
    statistics.averageDamagePerBattle = totalDamage / battlesCount;

    return statistics;
  },
);

export const clanRatingsSelector = createAppSelector(
  [membersBattleTypesSelector, membersTeamNumberSelector, clanMembersStatisticsSelector, statisticsSelector],
  (membersBattleTypes, membersTeamNumber, clanMembersStatistics, statistics) => {
    return {
      membersBattleTypes,
      membersTeamNumber,
      clanMembersStatistics,
      statistics,
    };
  },
);
